import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Archive, CalendarDays, LoaderCircle, Pencil, Plus, Settings, SlidersHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Nav, NavItem, NavLink, TabContent, TabPane } from '@/components/ui-compat'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import CalendarDialog from './CalendarDialog'
import ConfirmDialog from './ConfirmDialog'
import { describeScheduleError } from '../lib/errors'
import type { ScheduleCalendar } from '../types'

interface Props { open: boolean; onOpenChange: (open: boolean) => void; projectId: string; initialTab?: 'general' | 'calendars' }

const ScheduleSettingsDialog: React.FC<Props> = ({ open, onOpenChange, projectId, initialTab = 'general' }) => {
  const { t } = useTranslation()
  const { canEdit, canAdmin } = useModuleActions('SCHEDULE', projectId)
  const [tab, setTab] = useState<'general' | 'calendars'>(initialTab)
  const [threshold, setThreshold] = useState<string>('5')
  const [calDialog, setCalDialog] = useState<{ open: boolean; calendar?: ScheduleCalendar }>({ open: false })
  const [archiving, setArchiving] = useState<ScheduleCalendar | null>(null)
  const [archivingBusy, setArchivingBusy] = useState(false)

  const { data: settings } = useQuery(Query.SCHEDULE_SETTINGS, { variables: { projectId }, skip: !open })
  const { data: calData } = useQuery(Query.SCHEDULE_CALENDARS, { variables: { projectId }, skip: !open })
  const [updateSettings, { loading: saving }] = useMutation(Mutation.UPDATE_SCHEDULE_SETTINGS, {
    update: (cache, { data }) => {
      if (!data?.updateScheduleSettings) return
      cache.writeQuery({ query: Query.SCHEDULE_SETTINGS, variables: { projectId }, data: { scheduleSettings: data.updateScheduleSettings } })
    },
  })
  const [archiveCalendar] = useMutation(Mutation.ARCHIVE_SCHEDULE_CALENDAR, { refetchQueries: [{ query: Query.SCHEDULE_CALENDARS, variables: { projectId } }] })

  // Review fix (parked minor): depend on the primitive value, not the `settings` object — Apollo
  // hands back a new `settings` reference on every requery (including the one this dialog's own
  // `updateSettings` triggers via `cache.writeQuery`), which used to re-seed `threshold` even when
  // the underlying number hadn't changed, clobbering whatever the user was mid-typing.
  const slipThresholdDays = settings?.scheduleSettings?.slipThresholdDays
  useEffect(() => { if (slipThresholdDays != null) setThreshold(String(slipThresholdDays)) }, [slipThresholdDays])
  const thresholdNum = Number(threshold)
  const thresholdValid = Number.isInteger(thresholdNum) && thresholdNum >= 0 && thresholdNum <= 60
  const calendars: ScheduleCalendar[] = calData?.scheduleCalendars ?? []

  const saveThreshold = async () => {
    try {
      await updateSettings({ variables: { projectId, input: { slipThresholdDays: thresholdNum } } })
      toastr.success('', t('Schedule_settings_saved'))
    } catch (e: any) { toastr.error(t('Common_error'), describeScheduleError(e.message, t)) }
  }
  const runArchive = async () => {
    if (!archiving) return
    setArchivingBusy(true)
    try {
      await archiveCalendar({ variables: { projectId, id: archiving.id } })
      toastr.success('', t('Schedule_calendar_archived'))
      setArchiving(null)
    } catch (e: any) { toastr.error(t('Common_error'), describeScheduleError(e.message, t)) } finally { setArchivingBusy(false) }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !saving && !archivingBusy) onOpenChange(false) }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" />{t('Schedule_settings_title')}</DialogTitle></DialogHeader>
        <Nav tabs>
          <NavItem><NavLink className={cn('flex items-center', { active: tab === 'general' })} onClick={() => setTab('general')}><SlidersHorizontal className="h-4 w-4 mr-2" />{t('Schedule_settings_general')}</NavLink></NavItem>
          <NavItem><NavLink className={cn('flex items-center', { active: tab === 'calendars' })} onClick={() => setTab('calendars')}><CalendarDays className="h-4 w-4 mr-2" />{t('Schedule_calendars')}</NavLink></NavItem>
        </Nav>
        <TabContent activeTab={tab}>
          <TabPane tabId="general">
            <div className="space-y-4">
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="slip-threshold">{t('Schedule_settings_threshold')}</Label>
                <Input id="slip-threshold" type="number" min={0} max={60} value={threshold} onChange={(e) => setThreshold(e.target.value)} disabled={!canAdmin} className={cn(!thresholdValid && 'border-destructive')} />
                <p className="text-xs text-muted-foreground">{t('Schedule_settings_threshold_help')}</p>
              </div>
              {canAdmin && <div className="flex justify-end"><Button onClick={saveThreshold} disabled={!thresholdValid || saving}>{saving && <LoaderCircle className="h-4 w-4 animate-spin" />}{t('Common_save')}</Button></div>}
            </div>
          </TabPane>
          <TabPane tabId="calendars">
            <div className="space-y-3">
              {canEdit && <div className="flex justify-end"><Button size="sm" className="h-8" onClick={() => setCalDialog({ open: true })}><Plus className="h-4 w-4" />{t('Schedule_calendar_new')}</Button></div>}
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground"><tr><th className="py-1">{t('Schedule_field_name')}</th><th className="py-1">{t('Schedule_calendar_working_days')}</th><th className="py-1">{t('Schedule_calendar_exceptions')}</th><th /></tr></thead>
                <tbody>
                  {calendars.map((c) => (
                    <tr key={c.id} className={cn('border-t', c.archived && 'text-muted-foreground')}>
                      <td className="py-2">{c.name} {c.archived && <Badge variant="neutral" className="ml-2">{t('Schedule_status_ARCHIVED')}</Badge>}</td>
                      <td className="py-2"><span className="inline-flex gap-1">{[1, 2, 3, 4, 5, 6, 7].map((d) => <span key={d} className={cn('w-6 text-center rounded text-xs', c.workingWeekdays.includes(d) ? 'bg-primary/10 text-primary' : 'text-muted-foreground')}>{t(`Schedule_weekday_${d}`)}</span>)}</span></td>
                      <td className="py-2 tabular-nums">{c.exceptions.length}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {canEdit && !c.archived && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCalDialog({ open: true, calendar: c })}><Pencil className="h-4 w-4" /></Button>}
                        {canAdmin && !c.archived && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setArchiving(c)}><Archive className="h-4 w-4" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabPane>
        </TabContent>
        <div className="flex items-center justify-end pt-4 border-t"><Button variant="outline" onClick={() => onOpenChange(false)}>{t('Common_close')}</Button></div>
        <CalendarDialog open={calDialog.open} onOpenChange={(o) => setCalDialog((s) => ({ ...s, open: o }))} projectId={projectId} calendar={calDialog.calendar} />
        <ConfirmDialog open={!!archiving} title={t('Schedule_calendar_archive_title')} description={archiving ? t('Schedule_calendar_archive_body', { name: archiving.name }) : undefined} confirmLabel={t('Schedule_action_archive')} submitting={archivingBusy} onCancel={() => setArchiving(null)} onConfirm={runArchive} />
      </DialogContent>
    </Dialog>
  )
}
export default ScheduleSettingsDialog
